<template>
  <o-modal :active="isActive" :canCancel="false" class="modal-xs" :key="dataLoaded">
    <div  class="modal-card column-chooser">
      <header class="modal-card-head">
        <h4
          class="modal-card-title is-capitalized pl-2"
        >
          {{ getHeaderText}}
          <span class="required-label is-size-5 is-italic is-pulled-right mr-0 has-text-weight-normal">
            <span class="has-text-link">*</span> required
          </span>
        </h4>
      </header>
      <section class="modal-card-body is-marginless is-visible cf-modal-body">
        <div class="column p-0 mb-2">
          <div class="columns">
            <div class="column">
              <h3 class="is-size-3 has-text-black-bis has-text-weight-semibold is-italic mb-1">
                {{ getLabelText}}<span class="has-text-link">*</span>
              </h3>
              <input class="input" type="text" v-model.trim="cfLabel"  @input="cfLabel = $event.target.value" @blur="validateLabel()"/>
            </div>
          </div>
          <div class="columns">
            <div class="column" v-if="['dropdown', 'string'].includes(cfType)">
              <h3 class="is-size-3 has-text-black-bis has-text-weight-semibold is-italic mb-1">
                Enter Prompt/Helper Text<span class="has-text-link">*</span>
              </h3>
              <input class="input" type="text" v-model="promptText"  @input="promptText = $event.target.value"/>
            </div>
          </div>
          <div class="columns">
            <div class="column" v-if="cfType === 'string'">
              <h3 class="is-size-3 has-text-black-bis has-text-weight-semibold is-italic
                mb-1 is-flex is-justify-content-start is-align-items-center">
                <span class="mr-2">Enforced uniqueness</span>
                <span>
                  <span v-if="modificationType !== 'create'" class="is-italic has-text-weight-normal">{{ isUnique.value }}</span>
                  <mf-multi-select v-else
                    v-model="isUnique"
                    :options="uniquenessOpts"
                    track-by="id"
                    label="value"
                    :multiple="false"
                  ></mf-multi-select>
                </span>
              </h3>
            </div>
          </div>
        </div>
        <div v-if="cfType === 'dropdown'" class="column p-0">
          <h3 class="is-size-3 has-text-black-bis has-text-weight-semibold is-italic mb-1">Dropdown Menu Items (2 min./10 max.)
            <span class="has-text-link">*</span>
          </h3>
          <div class="chooser-body dropdown-item px-0">
            <draggable
              v-model="inputs"
              item-key="id"
              handle=".handle"
              :move="onMove"
            >
              <template #item="{ element }">
                <div class="column-chooser-item is-flex is-align-items-center">
                  <div class="column px-0">
                    <div class="columns">
                      <div class="column is-1">
                        <i class="icon-removedelete is-clickable" @click="inputs.splice(inputs.indexOf(element), 1)"></i>
                      </div>
                      <div class="column">
                        <input v-model="element.value" class="input"/>
                      </div>
                      <div class="column is-narrow">
                        <i class="icon-reorder handle"></i>                        
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>
            <i class="icon-addnew is-clickable" @click="addInput" v-if="inputs.length < 10"></i>
          </div>
        </div>
        <div class="is-divider"></div>
        <div class="column p-0">
          <div class="columns">
            <div class="column">
              <h5 class="is-size-5 has-text-link has-text-weight-semibold is-italic mb-3 mt-1">Preview</h5>
              <h3 class="is-size-3 has-text-grey-darker mb-1 line-height place-hoder-text">{{ $_.isEmpty(cfLabel) ? '' : cfLabel }}</h3>
              <span v-if="cfType === 'dropdown'">
                <mf-multi-select
                  :modelValue="null"
                  :options="inputs"
                  track-by="id"
                  label="value"
                  :multiple="false"
                  :placeholder="promptText"
                ></mf-multi-select>
              </span>
              <span v-else-if="cfType === 'checkbox'">
                <o-checkbox
                  v-model="isChecked"
                  disabled
                  class="pl-0"
                >
              </o-checkbox>
              </span>
              <span v-else-if="cfType === 'date'">
                <mf-date
                  :is-edit="true"
                  :input-props="{}"
                  :placeholder='promptText || "mm/dd/yyyy"'
                  disabled
                />
              </span>
              <span v-else-if="['number', 'string'].includes(cfType)">
                <input class="input" v-model="promptText" :type="cfType === 'number' ? 'number' : 'text'" disabled />
              </span>
            </div>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot is-justify-content-flex-end">
        <button class="button has-background-black-bis" @click="addCustomFields()" :disabled="disableAdd">
          {{ modificationType === 'create' ? 'Add' : 'Done' }}
        </button>
        <button class="button is-outlined has-text-black-bis" @click="$emit('close')">
          Cancel
        </button>
      </footer>
    </div>
  </o-modal>
</template>

<script>

import { isEmpty, isString } from 'lodash';
import { computed, onBeforeMount, reactive, toRefs } from 'vue';
import { useToast } from 'vue-toastification';
import draggable from 'vuedraggable';
import MfDate from '@/components/abstract/MfDate.vue';

export default {
  name: 'CustomFieldsModal',
  props: {
    isActive: {
      type: Boolean,
      require: true,
      default: false,
    },
    customField: {
      type: String,
      default: '',
    },
    modificationType: {
      type: String,
      default: '',
    },
    tabType: {
      type: String,
      default: '',
    },
    dataBeforeEdit: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    draggable,
    MfDate,
  },
  setup(props, { emit }) {
    const toast = useToast();
    const uniquenessOpts = [
      { id: 'none', value: 'None' },
      { id: 'company', value: 'Company-wide' },
      { id: 'project', value: 'Project-wide'}
    ];
    const state = reactive({
      dropdownOpts: [],
      cfLabel: '',
      promptText: '',
      inputs: [],
      isChecked: false,
      isUnique: uniquenessOpts[0],
      dataLoaded: 0,
      cfType: isString(props.customField) ? props.customField.toLowerCase() : props.customField.__t.toLowerCase(),
      labelsList: [],
    });

    const addInput = () => {
      if (state.inputs.length < 10) {
        state.inputs.push({ id: state.inputs.length + 1, value: '' });
      }
    };

    const addCustomFields = () => {
      if (isEmpty(state.cfLabel))  return;
      const labelExists = !isEmpty(state.labelsList) && state.labelsList.some((labelObj) => props.customField._id !== labelObj._id && labelObj.label === state.cfLabel);
      if (labelExists) {
        toast.warning(`A field with this label "${state.cfLabel}" already exists. Please enter a unique label.`);
        state.cfLabel = '';
        return;
      }
      let customFieldObj;
      customFieldObj = {
        type: state.cfType,
        label: state.cfLabel,
        __t: (fieldConfig[state.cfType]).labelText || '',
        isNew: isEmpty(props.customField?._id) || props.customField?._id?.startsWith('new-') ? true : false,
      };
      if (state.cfType === 'dropdown' && state.inputs.length < 2) return;
      if (['dropdown', 'string'].includes(state.cfType)) {
        customFieldObj.placeholder = state.promptText;
        if (state.cfType === 'dropdown') {
          customFieldObj.options = state.inputs.filter((input) => !isEmpty(input.value)).map((input) => {
            if (!isEmpty(input._id)) return { _id: input._id, displayValue: input.value };
            return { displayValue: input.value}; 
          });
        }
        if (state.cfType === 'string') {
          customFieldObj.uniqueAcross = state.isUnique.id;
        }
      }
      if (props.modificationType === 'edit' && !customFieldObj?.isNew) {
        if (!props.customField?._id?.startsWith('new-')) {
          const fieldsToUpdate = props.dataBeforeEdit.fields.find((field) => field._id === props.customField._id);
          customFieldObj = { ..._.pick(fieldsToUpdate, ['_id', 'archived', 'sqlColumnId']), ...customFieldObj};
          customFieldObj.isNew = false;
          customFieldObj.isEdited = true;
        }
      }
      if (['dropdown', 'string'].includes(state.cfType) && (state.promptText.length < 3 || state.promptText.length > 48)) {
        state.promptText = props.customField?.placeholder || '';
        toast.warning('Prompt text must be between 3 and 48 characters. Please enter a valid prompt text.');
        return;
      } else if (props.modificationType === 'edit' && props.customField?.isNew) {
        customFieldObj._id = props.customField._id;
        customFieldObj.isNew = true;
      }
      emit('add-custom-field', { newCfs: customFieldObj, oldCfs: props.dataBeforeEdit });
      emit('close');
    };

    const fieldConfig = {
      dropdown: { headerText: 'Dropdown Menu', labelText: 'Dropdown' },
      date: { headerText: 'Date Field', labelText: 'Date' },
      checkbox: { headerText: 'Checkbox Field', labelText: 'Checkbox' },
      number: { headerText: 'Number Field', labelText: 'Number' },
      string: { headerText: 'String Field', labelText: 'String' },
    };

    const getActionText = (action) => action === 'create' ? 'Create' : 'Edit';

    const getHeaderText = computed(() => {
      const config = fieldConfig[state.cfType];
      if(state.cfType === 'string') {
        return props.modificationType === 'create' ? 'Create Text Field' : 'Edit Text Field';
      }
      return config ? `${getActionText(props.modificationType)} ${config.headerText}` : '';
    });

    const getLabelText = computed(() => {
      const config = fieldConfig[state.cfType];
      if(state.cfType === 'string') {
        return props.modificationType === 'create' ? 'Enter Text Label' : 'Edit Text Label';
      }
      return config ? `Enter ${config.labelText} Label` : '';
    });

    const disableAdd = computed(() => {
      if (isEmpty(state.cfLabel)) return true;
      if (state.cfType === 'dropdown') return state.inputs.length < 2 || state.inputs.some((input) => isEmpty(input.value.trim()));
    });

    const validateLabel = () => {
      if (isEmpty(state.cfLabel)) return;
      const labelExists = !isEmpty(state.labelsList) && state.labelsList.some((labelObj) => props.customField._id !== labelObj._id && labelObj.label === state.cfLabel);
      if (labelExists) {
        toast.warning(`A field with this label "${state.cfLabel}" already exists. Please enter a unique label.`);
        return;
      }
      if (state.cfLabel.length < 3 || state.cfLabel.length > 24 ) {
        state.cfLabel = props.customField?.label || '';
        toast.warning('Label must be between 3 and 24 characters. Please enter a valid label.');
      }
      return;
    };

    onBeforeMount( async () => {
      props.dataBeforeEdit?.fields.forEach((field) => field.label && state.labelsList.push({label: field.label, _id: field._id}));
      if (props.modificationType === 'edit') {
        let fieldToEdit;
        if (props.customField.isNew) {
          fieldToEdit = props.customField;
        } else {
          if (props.customField?.isEdited) fieldToEdit = props.customField;
          else fieldToEdit = props.dataBeforeEdit.fields.find((field) => field._id === props.customField._id);
        }
        if (fieldToEdit) {
          state.cfLabel = fieldToEdit.label;
          if (['dropdown', 'string'].includes(state.cfType)) state.promptText = fieldToEdit.placeholder;
          if (state.cfType === 'checkbox') {
            state.isChecked = false;
          }
          if (state.cfType === 'dropdown') {
            state.inputs = fieldToEdit.options.map((opt, idx) => { return { id: idx + 1, value: opt.displayValue, _id: opt._id }; });
          }
          if (state.cfType === 'string') {
            state.isUnique = fieldToEdit.uniqueAcross ? uniquenessOpts.find((opt) => opt.id === fieldToEdit.uniqueAcross) : uniquenessOpts[0];
            state.cfLabel = fieldToEdit.label
            state.promptText = fieldToEdit.placeholder;
          }
        }
      }
      state.dataLoaded += 1;
    });

    return {
      ...toRefs(state),
      addInput,
      addCustomFields,
      getHeaderText,
      getLabelText,
      disableAdd,
      uniquenessOpts,
      validateLabel,
    };
  },
};
</script>

<style scoped>
 .modal-xs .modal-card {
   width: 360px !important;
 }
 .modal-card-body {
   overflow: hidden;
 }
 .modal-card-head {
  background-color: #fff !important;
  border-bottom: none !important;
}
.modal-card-foot {
border-top: 1px solid #84888c;
  background-color: #fff !important;
}
</style>